import React, { useState, useEffect } from "react";
import { getBranchAdminStats } from "../services/branch-admin";
import { getStudentStats } from "../services/students";
import { getTeacherStats } from "../services/teachers";
import { getClasses } from "../services/classes";
import {
  Users,
  GraduationCap,
  DollarSign,
  BookOpen,
  UserCheck,
  TrendingUp,
  AlertCircle,
  Clock,
  PlusCircle,
  Eye,
  Edit,
  FileText,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/common/Card";

interface DashboardStats {
  totalStudents: number;
  activeStudents: number;
  totalTeachers: number;
  activeTeachers: number;
  totalClasses: number;
  pendingAttendance: number;
  monthlyRevenue: number;
  pendingFees: number;
  recentActivities: Array<{
    id: string;
    type: string;
    message: string;
    timestamp: string;
  }>;
}

const currentDate = new Date().toLocaleDateString("en-US", {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
});

const BranchAdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalStudents: 0,
    activeStudents: 0,
    totalTeachers: 0,
    activeTeachers: 0,
    totalClasses: 0,
    pendingAttendance: 0,
    monthlyRevenue: 0,
    pendingFees: 0,
    recentActivities: [],
  });

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch data from multiple APIs in parallel
      const [studentStatsResult, teacherStatsResult, classesResult] =
        await Promise.allSettled([
          getStudentStats(),
          getTeacherStats(),
          getClasses({ page: 1, limit: 100 }), // Get all classes
        ]);

      // Process student stats
      const studentStats =
        studentStatsResult.status === "fulfilled"
          ? studentStatsResult.value?.data
          : { totalStudents: 0, activeStudents: 0 };

      // Process teacher stats
      const teacherStats =
        teacherStatsResult.status === "fulfilled"
          ? teacherStatsResult.value?.data
          : { totalTeachers: 0, activeTeachers: 0 };

      // Process classes
      const classesData =
        classesResult.status === "fulfilled"
          ? classesResult.value?.classes
          : null;

      const totalClasses = Array.isArray(classesData)
        ? classesData.length
        : (classesData as any)?.total || 0;

      const dashboardStats: DashboardStats = {
        totalStudents: studentStats.totalStudents || 0,
        activeStudents: studentStats.activeStudents || 0,
        totalTeachers: teacherStats.totalTeachers || 0,
        activeTeachers: teacherStats.activeTeachers || 0,
        totalClasses,
        pendingAttendance: 0, // TODO: Add attendance API
        monthlyRevenue: 0, // TODO: Add revenue API
        pendingFees: 0, // TODO: Add fees API
        recentActivities: [], // TODO: Add activities API
      };

      setStats(dashboardStats);
    } catch (err: any) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="animate-pulse space-y-8 max-w-7xl mx-auto">
          <div className="h-12 bg-white/40 backdrop-blur-sm rounded-2xl w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-32 bg-white/40 backdrop-blur-sm rounded-2xl"
              ></div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-48 bg-white/40 backdrop-blur-sm rounded-2xl"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-400 to-red-500 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <AlertCircle className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Error Loading Dashboard
            </h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="bg-white border-2 border-slate-200 hover:border-blue-300 text-slate-700 px-6 py-3 rounded-xl font-bold flex items-center gap-3 hover:shadow-lg hover:scale-105 transition-all duration-300 mx-auto"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-8">
          <div className="text-right">
            <p className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              {currentDate}
            </p>
          </div>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Branch Dashboard
              </h1>
              <p className="text-slate-600 font-medium mt-2">
                Welcome back, {user?.email}! Here's what's happening at your
                branch today.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Total Students
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.totalStudents}
                </p>
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-lg px-2 py-1">
                    <p className="text-xs font-bold text-white">
                      {stats.activeStudents} active
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-4 rounded-2xl shadow-lg">
                <Users className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Teachers
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.totalTeachers}
                </p>
                <div className="flex items-center gap-1.5 mt-2">
                  <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-lg px-2 py-1">
                    <p className="text-xs font-bold text-white">
                      {stats.activeTeachers} active
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-emerald-400 to-green-500 p-4 rounded-2xl shadow-lg">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Active Classes
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {stats.totalClasses}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-400 to-purple-500 p-4 rounded-2xl shadow-lg">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activities and Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activities */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <div className="bg-gradient-to-br from-slate-300 to-slate-400 p-2 rounded-xl shadow-sm">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                Recent Activities
              </h3>
            </div>
            <div className="space-y-3">
              {stats.recentActivities.length > 0 ? (
                stats.recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 hover:shadow-md transition-all"
                  >
                    <div className="flex-shrink-0">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${
                          activity.type === "student"
                            ? "bg-gradient-to-br from-blue-300 to-blue-400"
                            : activity.type === "attendance"
                            ? "bg-gradient-to-br from-emerald-300 to-emerald-400"
                            : activity.type === "fee"
                            ? "bg-gradient-to-br from-green-300 to-green-400"
                            : "bg-gradient-to-br from-purple-300 to-purple-400"
                        }`}
                      >
                        {activity.type === "student" && (
                          <Users className="w-5 h-5 text-white" />
                        )}
                        {activity.type === "attendance" && (
                          <UserCheck className="w-5 h-5 text-white" />
                        )}
                        {activity.type === "fee" && (
                          <DollarSign className="w-5 h-5 text-white" />
                        )}
                        {activity.type === "teacher" && (
                          <GraduationCap className="w-5 h-5 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">
                        {activity.message}
                      </p>
                      <p className="text-xs text-slate-500 font-medium mt-1">
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="bg-gradient-to-br from-slate-300 to-slate-400 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                  <p className="text-slate-600 font-medium">
                    No recent activities
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <div className="bg-gradient-to-br from-slate-300 to-slate-400 p-2 rounded-xl shadow-sm">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                Quick Stats
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-xl p-2 shadow-sm">
                    <UserCheck className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-bold text-slate-900">
                    Student Attendance Rate
                  </span>
                </div>
                <span className="text-lg font-bold text-emerald-600">
                  94.2%
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-2 shadow-sm">
                    <GraduationCap className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-bold text-slate-900">
                    Teacher Utilization
                  </span>
                </div>
                <span className="text-lg font-bold text-purple-600">92.1%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200/60 hover:shadow-md transition-all">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-amber-300 to-amber-400 rounded-xl p-2 shadow-sm">
                    <BookOpen className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-sm font-bold text-slate-900">
                    Active Classes Today
                  </span>
                </div>
                <span className="text-lg font-bold text-slate-900">8/12</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchAdminDashboard;
